import { UiEventType } from "type/ui-event-type";
import { UiEvent, UiEventBatchRequest } from "model/ui-event";
import * as urls from "api/urls";
import * as credentials from "shared/credentials";

type QueuedUiEvent = UiEvent & { retryCount?: number };

class AnalyticsService {
	// UI Events queue for backend submission
	private uiEventQueue: QueuedUiEvent[] = [];
	private isSubmittingUiEvents = false;
	private readonly MAX_RETRIES = 3;

	// ---------- UI Event Tracking (Backend API) ----------

	/**
	 * Track a UI event (view, sort, etc.) - queued and sent to backend API
	 * These events are sent to the backend which forwards them to Mixpanel
	 * Backend automatically adds agent_id and ip_address
	 */
	trackUiEvent(eventType: UiEventType, properties?: Record<string, any>) {
		const event: UiEvent = {
			eventId: eventType.id,
			timestamp: new Date().toISOString(),
			properties,
		};

		this.uiEventQueue.push(event);

		// Submit immediately if we have enough events or after a delay
		if (this.uiEventQueue.length >= 10) {
			this.submitUiEvents();
		} else if (this.uiEventQueue.length === 1) {
			// Set a timer for the first event in queue
			setTimeout(() => this.submitUiEvents(), 5000);
		}
	}

	/**
	 * Submit queued UI events to the backend
	 */
	private async submitUiEvents() {
		if (this.isSubmittingUiEvents || this.uiEventQueue.length === 0) {
			return;
		}

		this.isSubmittingUiEvents = true;

		try {
			// Take up to 100 events from the queue (API limit)
			const eventsToSubmit = this.uiEventQueue.splice(0, 100);

			const requestBody: UiEventBatchRequest = {
				platform: "control-panel",
				events: eventsToSubmit,
			};

			// Get credentials for Basic Authentication
			const auth = credentials.get();
			if (!auth) {
				return;
			}
			const encoded = btoa(`${auth.username}:${auth.password}`);

			const url = urls.uiEvents();

			const response = await fetch(url, {
				method: "POST",
				headers: {
					"Authorization": `Basic ${encoded}`,
					"Content-Type": "application/json",
					"X-Source": "controlPanel",
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				// Try to get error response body
				let errorBody = null;
				try {
					errorBody = await response.json();
				} catch(e) {
					try {
						errorBody = await response.text();
					} catch(e2) {
						// Ignore
					}
				}

				// eslint-disable-next-line no-console
				console.error("[UI Events] Failed to submit:", {
					status: response.status,
					statusText: response.statusText,
					url,
					errorBody,
					requestSent: requestBody
				});

				// Only retry if we haven't exceeded max retries
				const eventsToRetry = eventsToSubmit.filter(event => {
					const retryCount = (event.retryCount || 0) + 1;
					if (retryCount <= this.MAX_RETRIES) {
						event.retryCount = retryCount;
						return true;
					} else {
						// eslint-disable-next-line no-console
						console.warn("[UI Events] Event dropped after max retries:", event);
						return false;
					}
				});

				// Put events back in queue only if they haven't exceeded retry limit
				if (eventsToRetry.length > 0) {
					this.uiEventQueue.unshift(...eventsToRetry);
				}
			} else {
				const result = await response.json();
				if (result.rejected > 0) {
					// eslint-disable-next-line no-console
					console.warn(`[UI Events] Partial success: ${result.accepted} accepted, ${result.rejected} rejected`, result.errors);
				}
			}
		} catch(error) {
			// eslint-disable-next-line no-console
			console.error("[UI Events] Error submitting UI events:", error);
			// Network errors - events remain in queue for retry but with same retry logic
		} finally {
			this.isSubmittingUiEvents = false;

			// If there are more events in queue, submit them
			if (this.uiEventQueue.length > 0) {
				setTimeout(() => this.submitUiEvents(), 1000);
			}
		}
	}

	/**
	 * Force submit all queued UI events immediately
	 * Useful for when app is backgrounding or closing
	 */
	async flushUiEvents() {
		await this.submitUiEvents();
	}

	// ---------- Convenience methods for common UI events ----------

	trackViewLeadDetail(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_LEAD_DETAIL, { lead_id: leadId });
	}

	trackViewActivity(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_ACTIVITY, { lead_id: leadId });
	}

	trackViewSubscriptions(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_SUBSCRIPTIONS, { lead_id: leadId });
	}

	trackViewEmailAlertActivity(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_EMAIL_ALERT_ACTIVITY, { lead_id: leadId });
	}

	trackViewPropertyOrganizer(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_PROPERTY_ORGANIZER, { lead_id: leadId });
	}

	trackViewSearchAgent(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_SEARCH_AGENT, { lead_id: leadId });
	}

	trackViewSearchAgentConversation(leadId: number, date: string) {
		this.trackUiEvent(UiEventType.VIEW_SEARCH_AGENT_CONVERSATION, {
			lead_id: leadId,
			conversation_date: date,
		});
	}

	trackViewFavoriteListings(leadId: number) {
		this.trackUiEvent(UiEventType.VIEW_FAVORITE_LISTINGS, { lead_id: leadId });
	}

	trackSortFavoriteListings(leadId: number, sortField: string, sortDirection: "asc" | "desc") {
		this.trackUiEvent(UiEventType.SORT_FAVORITE_LISTINGS, {
			lead_id: leadId,
			sort_field: sortField,
			sort_direction: sortDirection,
		});
	}
}

export const mixpanelAnalytics = new AnalyticsService();
